import { transformResponseFromJsonApi } from '@evolve/common-ui';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  Request,
  Response,
  TQueryParams,
  formatQueryParams,
  formatRequest,
  prepareHeaders,
} from 'evolve-ui';
import { TEmployeeWithProfile } from './employees.api';

export enum CustomerContactStatus {
  POTENTIAL = 1,
  ACTIVE = 2,
  INACTIVE = 3,
  EXCLUDED = 4,
}

export enum ContactCategory {
  CUSTOMERS = 'customers',
  EMPLOYEES = 'employees',
}

export type TContact = {
  _id?: string;
  id?: number;
  name?: string;
  email?: string;
  phone?: string;
  anonymized?: boolean;
  data?: {
    // * RECRUITMENT
    age?: number;
    birthDate?: string;
    code?: string;
    costCenter?: {
      id?: number;
      reference?: string;
      description?: string;
      customer_id?: number;
      agency?: {
        acronym?: string;
        active?: number;
        address?: string;
        id?: number;
        location?: string;
        name?: string;
        postalCode?: string;
      };
    };
    gender?: number;
    location?: {
      active?: number;
      id?: number;
      name?: number;
    };
    postalCode?: string;

    // * CRM
    address?: { id?: number; locationId?: number; postalCode?: string };
    customer?: {
      id?: number;
      name?: string;
      number?: number;
      customerStatusId?: number;
      caCodeId?: number;
      caCode?: {
        id?: number;
        name?: string;
        activitySectorId?: number;
      };
      agencyCustomerUser?: {
        id?: number;
        userPrimaryCustomer?: number;
        agencyId?: number;
        customerId?: number;
        userId?: number;
      };
    };
    consents: {
      marketing: boolean;
      offers: boolean;
      anonymized: boolean;
    };
  };
};

// Helper function to transform TEmployeeWithProfile to TContact
const transformEmployeeToContact = (
  employee: TEmployeeWithProfile,
): TContact => {
  return {
    _id: employee.id,
    id: parseInt(employee.portalId, 10),
    name: employee.profile?.name,
    email: employee.profile?.email,
    phone: employee.profile.cellphone.number,
    data: {
      code: employee.code,
    },
  };
};

export const contactsApi = createApi({
  reducerPath: 'contacts',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.GATSBY_CONTACTS_API_URL}`,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['Contacts'],
  endpoints: (builder) => ({
    // * GET

    getContactsOld: builder.query<
      Response<TContact>,
      TQueryParams & { category: ContactCategory }
    >({
      providesTags: ['Contacts'],
      query: ({ category, ...params }) => {
        return formatQueryParams(params, `${category}`);
      },
      transformResponse: transformResponseFromJsonApi<TContact>,
    }),
    getContacts: builder.query<
      Response<TContact>,
      TQueryParams & { category: ContactCategory }
    >({
      providesTags: ['Contacts'],
      queryFn: async (
        { category, ...params },
        api,
        extraOptions,
        baseQuery,
      ) => {
        // If category is employees (recruitment), use employees API
        if (category === ContactCategory.EMPLOYEES) {
          const employeesBaseQuery = fetchBaseQuery({
            baseUrl: `${process.env.GATSBY_EMPLOYEES_API_URL}`,
            mode: 'cors',
            prepareHeaders,
          });

          const result = await employeesBaseQuery(
            formatQueryParams(params, 'advanced-search'),
            api,
            extraOptions,
          );

          if (result.error) {
            return { error: result.error };
          }

          // Transform employees response to contacts response
          const employeesResponse =
            transformResponseFromJsonApi<TEmployeeWithProfile>(
              result.data as any,
            );

          const contactsResponse: Response<TContact> = {
            ids: employeesResponse.ids,
            total: employeesResponse.total,
            values: employeesResponse.values.map(transformEmployeeToContact),
          };

          return { data: contactsResponse };
        }

        // If category is customers (CRM), use current contacts API
        const result = await baseQuery(
          formatQueryParams(params, `${category}`),
        );

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: transformResponseFromJsonApi<TContact>(result.data as any),
        };
      },
    }),

    patchContacts: builder.mutation<
      Response<TContact>,
      Partial<Pick<TContact, 'email' | 'phone'>> & {
        id: string;
        category: ContactCategory;
      }
    >({
      invalidatesTags: ['Contacts'],
      query: ({ id, category, ...payload }) => {
        return formatRequest(
          { ...payload, type: 'contacts' },
          Request.PATCH,
          `${category}/${id}`,
        );
      },
      transformResponse: transformResponseFromJsonApi<TContact>,
    }),
  }),
});

export const {
  useGetContactsQuery,
  useGetContactsOldQuery,
  usePatchContactsMutation,
} = contactsApi;
